import { accountIdFormat } from "@redotech/redo-account-id";
import { productIdFormat } from "@redotech/redo-product-id";
import { unaryRpc } from "@redotech/rpc/definition";
import * as z from "zod";

const ProductTransformAccountSchema_ = z.object({
  id: accountIdFormat.schema,
  name: z.string().nullable(),
  isCurrentAccount: z.boolean(),
});

export const ProductTransformAccountSchema: z.ZodType<ProductTransformAccount> =
  ProductTransformAccountSchema_;
export interface ProductTransformAccount extends z.infer<
  typeof ProductTransformAccountSchema_
> {}

/** The team's own account first, then its connected recommerce destinations. */
export const getProductTransformAccounts = unaryRpc({
  name: "getProductTransformAccounts" as const,
  input: z.object({}),
  output: z.object({ accounts: z.array(ProductTransformAccountSchema) }),
});

/** Account whose products a mapping targets; defaults to the team's own. */
const targetAccountId = accountIdFormat.schema.optional();

const ProductTransformMappingSchema_ = z.object({
  sourceProductId: productIdFormat.schema,
  sourceTitle: z.string(),
  sourceSku: z.string().nullable(),
  targetProductId: productIdFormat.schema,
  targetTitle: z.string(),
  targetSku: z.string().nullable(),
});

export const ProductTransformMappingSchema: z.ZodType<ProductTransformMapping> =
  ProductTransformMappingSchema_;
export interface ProductTransformMapping extends z.infer<
  typeof ProductTransformMappingSchema_
> {}

export const getProductTransformMappings = unaryRpc({
  name: "getProductTransformMappings" as const,
  input: z.object({ targetAccountId }),
  output: z.object({ mappings: z.array(ProductTransformMappingSchema) }),
});

export const createProductTransformMapping = unaryRpc({
  name: "createProductTransformMapping" as const,
  input: z.object({
    sourceProductId: productIdFormat.schema,
    targetProductId: productIdFormat.schema,
    targetAccountId,
    /** Source whose mapping into the target account this one replaces. */
    previousSourceProductId: productIdFormat.schema.optional(),
  }),
  output: z.object({ mapping: ProductTransformMappingSchema }),
  mock: (_generated, input) => ({
    mapping: {
      sourceProductId: input.sourceProductId,
      sourceTitle: "",
      sourceSku: null,
      targetProductId: input.targetProductId,
      targetTitle: "",
      targetSku: null,
    },
  }),
});

export const MAX_BULK_PRODUCT_TRANSFORM_MAPPINGS = 500;

export const createProductTransformMappings = unaryRpc({
  name: "createProductTransformMappings" as const,
  input: z.object({
    mappings: z
      .array(
        z.object({
          sourceProductId: productIdFormat.schema,
          targetProductId: productIdFormat.schema,
        }),
      )
      .min(1)
      .max(MAX_BULK_PRODUCT_TRANSFORM_MAPPINGS),
    targetAccountId,
  }),
  output: z.object({
    created: z.array(ProductTransformMappingSchema),
    failed: z.array(
      z.object({
        sourceProductId: productIdFormat.schema,
        targetProductId: productIdFormat.schema,
        reason: z.enum(["conflict", "not_found"]),
      }),
    ),
  }),
  mock: (_generated, input) => ({
    created: input.mappings.map((mapping) => ({
      sourceProductId: mapping.sourceProductId,
      sourceTitle: "",
      sourceSku: null,
      targetProductId: mapping.targetProductId,
      targetTitle: "",
      targetSku: null,
    })),
    failed: [],
  }),
});

export const suggestProductTransformMappings = unaryRpc({
  name: "suggestProductTransformMappings" as const,
  input: z.object({ pattern: z.string(), targetAccountId }),
  output: z.object({
    suggestions: z.array(ProductTransformMappingSchema),
    skipped: z.object({
      ambiguousSkuCount: z.number().int(),
      alreadyMappedCount: z.number().int(),
    }),
  }),
});

export const deleteProductTransformMapping = unaryRpc({
  name: "deleteProductTransformMapping" as const,
  input: z.object({ sourceProductId: productIdFormat.schema, targetAccountId }),
  output: z.object({ ok: z.literal(true) }),
  mock: { ok: true as const },
});

const TransformMappingSearchProductSchema_ = z.object({
  id: productIdFormat.schema,
  title: z.string(),
  sku: z.string().nullable(),
  /** Already mapped in a way that rules the product out for this side. */
  isMapped: z.boolean(),
});

export const TransformMappingSearchProductSchema: z.ZodType<TransformMappingSearchProduct> =
  TransformMappingSearchProductSchema_;
export interface TransformMappingSearchProduct extends z.infer<
  typeof TransformMappingSearchProductSchema_
> {}

export const searchProductsForTransformMapping = unaryRpc({
  name: "searchProductsForTransformMapping" as const,
  input: z.object({
    query: z.string(),
    limit: z.number().int().min(1).max(100).optional(),
    /** Sources are the team's products; targets belong to the target account. Defaults to target. */
    side: z.enum(["source", "target"]).optional(),
    targetAccountId,
  }),
  output: z.object({ products: z.array(TransformMappingSearchProductSchema) }),
});
